import { createContext, ReactNode, useState, useMemo, useCallback } from 'react';

import { Divider } from '@mui/material';
import htmlDocx from 'html-docx-js/dist/html-docx';

import { isMobile } from '@src/utils';
import { htmlToRtf, triggerFileDownload } from '@src/helpers';

interface NotesContextValue {
  text: string;
  setText: (text: string) => void;
  header: JSX.Element;
  saveEditor: (fallback?: string) => void;
  isSaveButtonDisabled: boolean;
}

const NotesContextDefaultValues: NotesContextValue = {
  text: '',
  setText: () => {},
  header: <></>,
  saveEditor: (fallback) => {},
  isSaveButtonDisabled: false,
};

const renderHeader = () => (
  <span className="w-full flex justify-between">
    <button className="ql-direction editor-tool-button" aria-label="Direction" value="rtl" />
    <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#42444C', marginBlock: '3px' }} />

    <button className="ql-bold editor-tool-button" aria-label="Bold" />
    <button className="ql-italic editor-tool-button" aria-label="Italic" />
    <button className="ql-strike editor-tool-button" aria-label="Strike" />
    <button className="ql-underline editor-tool-button" aria-label="Underline" />
    <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#42444C', marginBlock: '3px' }} />

    <button className="ql-list editor-tool-button" aria-label="List" value="bullet" />
    <button className="ql-list editor-tool-button" aria-label="List" value="ordered" />
    <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#42444C', marginBlock: '3px' }} />

    <button className="ql-indent editor-tool-button" aria-label="Indent" value="+1" />
    <button className="ql-indent editor-tool-button" aria-label="Indent" value="-1" />
    <button className="ql-image editor-tool-button" aria-label="Image" />
    <button className="ql-link editor-tool-button" aria-label="Link" />
    <Divider orientation="vertical" flexItem sx={{ backgroundColor: '#42444C', marginBlock: '3px' }} />

    <button className="ql-table-better editor-tool-button" aria-label="Table" />
  </span>
);

export const NotesContext = createContext<NotesContextValue>(NotesContextDefaultValues);

export const NotesContextProvider = ({ children }: { children: ReactNode }) => {
  const [text, setText] = useState<string>('');
  const header = useMemo(() => renderHeader(), []);
  const isSaveButtonDisabled = useMemo(() => !text.trim().length || !text.length, [text]);

  const AlterHTML = useCallback((htmlString: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const tableCells = tempDiv.querySelectorAll('td');
    tableCells.forEach((cell) => {
      cell.style.border = `2px solid #161d20`;
      if (!cell.firstElementChild?.textContent?.length && cell.firstElementChild) {
        cell.firstElementChild.innerHTML = '_';
      }
    });

    const images = tempDiv.querySelectorAll('img');
    images.forEach((img) => {
      const scaleFactor = 3;
      const width = Math.floor(img.width / scaleFactor);
      const height = Math.floor(img.height / scaleFactor);
      img.width = width;
      img.height = height;
    });

    const texts = tempDiv.querySelectorAll('span');
    texts.forEach((text) => {
      text.style.color = 'black';
      text.style.backgroundColor = 'transparent';
    });

    const bolds = tempDiv.querySelectorAll('strong');
    bolds.forEach((bold) => {
      bold.style.color = 'black';
      bold.style.backgroundColor = 'transparent';
    });

    const italics = tempDiv.querySelectorAll('em');
    italics.forEach((italic) => {
      italic.style.color = 'black';
      italic.style.backgroundColor = 'transparent';
    });

    const strikes = tempDiv.querySelectorAll('s');
    strikes.forEach((strike) => {
      strike.style.color = 'black';
      strike.style.backgroundColor = 'transparent';
    });

    const underlines = tempDiv.querySelectorAll('u');
    underlines.forEach((underline) => {
      underline.style.color = 'black';
      underline.style.backgroundColor = 'transparent';
    });

    return tempDiv.innerHTML;
  }, []);

  const saveEditor = useCallback(async () => {
    const content = text.trim();
    if (!content) return;

    const now = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    let fileName = `Notes_${pad(now.getMonth() + 1)}_${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(now.getSeconds())}`;

    try {
      // Use RTF format for all mobile devices (iOS and Android) with proper Unicode support
      if (isMobile(navigator.userAgent)) {
        // Mobile devices → RTF with Unicode support
        const processedContent = AlterHTML(content);
        const rtfContent = htmlToRtf(processedContent);

        const rtfBlob = new Blob([rtfContent], { type: 'application/rtf' });
        fileName += '.rtf';
        const rtfUrl = URL.createObjectURL(rtfBlob);
        triggerFileDownload(rtfUrl, fileName);
        return;
      }

      // Desktop → DOCX
      const wrappedHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8" />
            <style>
              body { font-family: Arial; }
              table { border-collapse: collapse; width: 100%; }
              td, th { border: 1px solid #ddd; padding: 8px; }
            </style>
          </head>
          <body>${AlterHTML(content)}</body>
        </html>
      `.trim();

      const docxBlob = htmlDocx.asBlob(wrappedHtml);
      fileName += '.docx';
      const url = URL.createObjectURL(docxBlob);
      triggerFileDownload(url, fileName);
      return;
    } catch (err) {
      // Fallback → HTML
      console.warn('Download failed, fallback to HTML', err);
      const htmlBlob = new Blob([content], { type: 'text/html' });
      fileName += '.html';
      const url = URL.createObjectURL(htmlBlob);
      triggerFileDownload(url, fileName);
    }
  }, [text, AlterHTML]);

  return (
    <NotesContext.Provider
      value={{
        text,
        header,
        isSaveButtonDisabled,

        setText,
        saveEditor,
      }}
    >
      {children}
    </NotesContext.Provider>
  );
};
